import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import ConfirmPopup from '../../component/common/modals/ConfirmPopup.js';
import OfferingCard from './OfferingCard.jsx';
import { errorToast, successToast } from '../../utils/helper.js';
import {
  getCustomOfferings,
  deleteCustomOffering,
  promoteAdminCustomOfferingToTop
} from '../../api/offeringApi.js';
import { Api } from '../../api/index.js';
import Pagination from '../../component/common/Pagination/Pagination.js';
import { PER_PAGE } from '../../utils/constants.js';

const B2BPerks = () => {
  const navigate = useNavigate();
  const [customPerks, setCustomPerks] = useState([]);
  const [loadingPerks, setLoadingPerks] = useState(false);
  const [filteredPerks, setFilteredPerks] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPerPage, setSelectedPerPage] = useState(PER_PAGE[0]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [companySearchText, setCompanySearchText] = useState('');
  const [isCompanyDropdownOpen, setIsCompanyDropdownOpen] = useState(false);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [companyCurrentPage, setCompanyCurrentPage] = useState(1);
  const [companyHasMore, setCompanyHasMore] = useState(true);
  const [perkToDelete, setPerkToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [actionMenuId, setActionMenuId] = useState('');
  const companyDropdownRef = useRef(null);

  // Fetch companies on component mount
  useEffect(() => {
    fetchCompanies(1, '', false);
  }, []);

  // Fetch perks when company is selected
  useEffect(() => {
    if (selectedCompany) {
      fetchCustomPerks();
    }
  }, [selectedCompany]);

  // Handle company search with debounce
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (isCompanyDropdownOpen) {
        fetchCompanies(1, companySearchText, false);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [companySearchText]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(event.target)) {
        setIsCompanyDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchCustomPerks = async () => {
    if (!selectedCompany) return;

    setLoadingPerks(true);
    try {
      const params = {};

      // Add query parameters based on selection
      if (selectedCompany._id === 'b2c') {
        params.isB2C = 'true';
      } else {
        params.companyId = selectedCompany._id;
      }

      const response = await getCustomOfferings(params);
      if (response?.data) {
        setCustomPerks(response.data);
        setFilteredPerks(response.data);
        console.log('Custom Perks:', response.data);
      }
    } catch (error) {
      console.error('Error fetching custom perks:', error);
      errorToast('Failed to load custom perks');
    } finally {
      setLoadingPerks(false);
    }
  };

  const fetchCompanies = async (page = 1, searchKey = '', isAppending = false) => {
    if (companyLoading) return;

    setCompanyLoading(true);
    try {
      const response = await Api.getAllCompanies(page, 10, searchKey, 'companyName', 1);

      if (response?.data?.meta?.code === 1) {
        const newCompanies = response.data.data || [];
        const totalCount = response.data.meta?.totalRecords || 0;

        if (isAppending) {
          setCompanies((prev) => [...prev, ...newCompanies]);
        } else {
          setCompanies(newCompanies);
        }

        setCompanyCurrentPage(page);
        setCompanyHasMore(
          newCompanies.length === 0 ? false : companies.length + newCompanies.length < totalCount
        );
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      errorToast('Failed to load companies');
    } finally {
      setCompanyLoading(false);
    }
  };

  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
    setIsCompanyDropdownOpen(false);
    setCompanySearchText('');
  };

  const handleCompanySearch = (e) => {
    setCompanySearchText(e.target.value);
    setCompanyCurrentPage(1);
  };

  const handleCompanyScroll = useCallback(
    (e) => {
      const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
      if (bottom && companyHasMore && !companyLoading) {
        fetchCompanies(companyCurrentPage + 1, companySearchText, true);
      }
    },
    [companyHasMore, companyLoading, companyCurrentPage, companySearchText]
  );

  const handleClearCompanyFilter = () => {
    setSelectedCompany(null);
    setCompanySearchText('');
    setCustomPerks([]);
    setFilteredPerks([]);
  };

  // Pagination logic
  const getPaginatedPerks = () => {
    const startIndex = (currentPage - 1) * selectedPerPage.value;
    const endIndex = startIndex + selectedPerPage.value;
    return filteredPerks.slice(startIndex, endIndex);
  };

  const handlePerPage = (perPage) => {
    setSelectedPerPage(perPage);
    setCurrentPage(1);
  };

  const handlePagination = (page) => {
    setCurrentPage(page);
  };

  const handleDeletePerk = (perk) => {
    setPerkToDelete(perk);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!perkToDelete?._id) return;

    setIsDeleting(true);
    try {
      const response = await deleteCustomOffering(perkToDelete._id);

      if (response?.meta?.code === 200 || response?.status === 200) {
        successToast('Perk deleted successfully');
        setShowDeleteConfirm(false);
        setPerkToDelete(null);
        // Refresh perks list if company is selected
        if (selectedCompany) {
          fetchCustomPerks();
        }
      } else {
        errorToast(response?.message || 'Failed to delete perk');
      }
    } catch (error) {
      console.error('Error deleting perk:', error);
      errorToast(error?.message || 'Failed to delete perk');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setPerkToDelete(null);
  };

  const handlePromoteToggle = async (offeringId) => {
    try {
      await promoteAdminCustomOfferingToTop(offeringId);
      successToast('Perk promotion status updated successfully');
      // Refresh perks to get updated data
      if (selectedCompany) {
        fetchCustomPerks();
      }
    } catch (error) {
      console.error('Error toggling promote:', error);
      errorToast('Failed to update promotion status');
      throw error;
    }
  };

  const paginatedPerks = getPaginatedPerks();

  return (
    <div>
      {/* Company Filter Dropdown - Always visible */}
      <div className="mb-6 flex gap-3">
        <div className="relative w-full sm:w-96" ref={companyDropdownRef}>
          <button
            type="button"
            onClick={() => setIsCompanyDropdownOpen(!isCompanyDropdownOpen)}
            className="w-full flex items-center justify-between px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 hover:border-gray-400 dark:hover:border-gray-500 transition-colors"
          >
            <div className="flex items-center gap-3 flex-1">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                <svg
                  className="w-4 h-4 text-blue-600 dark:text-blue-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
              </div>
              <div className="text-left flex-1 min-w-0">
                {selectedCompany ? (
                  <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                    {selectedCompany._id === 'b2c'
                      ? 'B2C (Direct Users)'
                      : selectedCompany.company_name}
                  </p>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">Filter by company...</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {selectedCompany && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearCompanyFilter();
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  ✕
                </button>
              )}
              <svg
                className={`w-4 h-4 text-gray-400 transition-transform ${
                  isCompanyDropdownOpen ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </button>

          {/* Dropdown Menu */}
          {isCompanyDropdownOpen && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-50 max-h-80 flex flex-col">
              {/* Search Input */}
              <div className="p-3 border-b border-gray-200 dark:border-gray-600">
                <input
                  type="text"
                  placeholder="Search companies..."
                  value={companySearchText}
                  onChange={handleCompanySearch}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
              </div>

              {/* Company List */}
              <div className="overflow-y-auto flex-1" onScroll={handleCompanyScroll}>
                {/* B2C Option at the top */}
                <button
                  type="button"
                  onClick={() =>
                    handleCompanySelect({ _id: 'b2c', company_name: 'B2C (Direct Users)' })
                  }
                  className={`w-full flex items-center gap-3 px-4 py-2 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors text-left border-b border-gray-200 dark:border-shoorah-darkBorder ${
                    selectedCompany?._id === 'b2c' ? 'bg-purple-50 dark:bg-purple-900/20' : ''
                  }`}
                >
                  <div className="flex-shrink-0 w-8 h-8 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center">
                    <svg
                      className="w-4 h-4 text-purple-600 dark:text-purple-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-purple-600 dark:text-purple-400">
                      B2C (Direct Users)
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Users not associated with any company
                    </p>
                  </div>
                  {selectedCompany?._id === 'b2c' && (
                    <svg
                      className="w-4 h-4 text-purple-600 dark:text-purple-400 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </button>

                {companyLoading && companies.length === 0 ? (
                  <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                    Loading companies...
                  </div>
                ) : companies.length === 0 ? (
                  <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                    No companies found
                  </div>
                ) : (
                  <>
                    {companies.map((company) => (
                      <button
                        key={company._id}
                        type="button"
                        onClick={() => handleCompanySelect(company)}
                        className={`w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left ${
                          selectedCompany?._id === company._id
                            ? 'bg-blue-50 dark:bg-blue-900/20'
                            : ''
                        }`}
                      >
                        <div className="flex-shrink-0 w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center overflow-hidden">
                          {company.company_logo ? (
                            <img
                              src={company.company_logo}
                              alt={company.company_name}
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            <svg
                              className="w-4 h-4 text-gray-600 dark:text-gray-300"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                              />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {company.company_name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {company.company_email || 'No email'}
                          </p>
                        </div>
                        {selectedCompany?._id === company._id && (
                          <svg
                            className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </button>
                    ))}
                    {companyLoading && (
                      <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                        Loading more...
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Content Area */}
      {loadingPerks ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading custom perks...</p>
          </div>
        </div>
      ) : !selectedCompany ? (
        <div className="bg-white dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 rounded-lg p-12 text-center">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-blue-600 dark:text-blue-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Select a Company to View Perks
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Choose a company from the dropdown above or select B2C to view perks for direct users.
            </p>
            <button
              onClick={() => navigate('/offering/create')}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <PlusCircleIcon className="w-5 h-5 mr-2" />
              Create Custom Perk
            </button>
          </div>
        </div>
      ) : filteredPerks.length === 0 ? (
        <div className="bg-white dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            No perks found for the selected{' '}
            {selectedCompany._id === 'b2c' ? 'B2C users' : 'company'}.
          </p>
          <button
            onClick={() => navigate('/offering/create')}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusCircleIcon className="w-5 h-5 mr-2" />
            Create Custom Perk
          </button>
        </div>
      ) : (
        <>
          <div className="bg-white dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 rounded-lg p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {paginatedPerks.map((perk) => (
                <OfferingCard
                  key={perk._id}
                  offering={perk}
                  actionMenuId={actionMenuId}
                  setActionMenuId={setActionMenuId}
                  onEdit={(perkItem) => {
                    navigate(`/offering/create?id=${perkItem.id}`);
                  }}
                  onDelete={handleDeletePerk}
                  onManage={(perkItem) => {
                    navigate(`/offering/manage/${perkItem.id}`);
                  }}
                  onPromoteToggle={handlePromoteToggle}
                />
              ))}
            </div>
          </div>

          {/* Pagination */}
          <div className="mt-4">
            {filteredPerks.length > selectedPerPage.value ? (
              <Pagination
                currentPage={currentPage}
                totalCount={filteredPerks.length}
                pageSize={selectedPerPage.value}
                onPageChange={handlePagination}
              />
            ) : null}
          </div>
        </>
      )}

      <ConfirmPopup
        open={showDeleteConfirm}
        setOpen={setShowDeleteConfirm}
        setAccepted={handleConfirmDelete}
        handleNo={handleCancelDelete}
        message={`Are you sure you want to delete \"${perkToDelete?.title}\"? This action cannot be undone.`}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default B2BPerks;
